import { Button } from "primereact/button";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import styled from "styled-components";
import {
  BenefitServiceLine,
  BenefitTierPreference,
} from "types/BenefitSelection/BenefitSelection";

import { BENEFIT_LABELS, serviceLineLabel, tierLabel } from "./constants";

const StyledDataTable = styled(DataTable)`
  .p-datatable-thead > tr > th {
    background: #ffffff;
  }
`;

interface IReadsRow {
  benefit: string;
  label: string;
  reads: string;
  onError: string;
  /** Which benefit the "How it is selected" drawer should open on. Null for the MRI note row. */
  explainFor: string | null;
}

/**
 * The result of every choice on the card, in one read-only table.
 *
 * 🚨 THERE IS NO "WHY" COLUMN, ON PURPOSE. A one-line reason next to each row would be a second,
 * shorter account of the engine's behaviour maintained by hand beside the generated one — and the
 * short version is the one that goes wrong first. The "How it is selected" link opens the
 * explanation the API renders from the saved configuration; that is the only account of it.
 */
export const buildReadsRows = (
  serviceLine: BenefitServiceLine | null,
  physicianCopayBilled: boolean,
  tierPreference: BenefitTierPreference
): IReadsRow[] => {
  const line = serviceLineLabel(serviceLine);
  const unset = serviceLine === null;

  /**
   * The tier, said only where it changes the answer and only on the benefits that HAVE a tier step.
   *
   * 🚨 THREE BENEFITS TIER, NOT FIVE. The copay's chain begins with a tier step and the two
   * plan-level balances have their lines tier-filtered before anything is selected
   * (`engine.ts` — `tierByBenefit`); co-insurance and the physician copay have no tier step at all,
   * so printing a tier beside them would state a rule the engine does not run. `Tier2` is left
   * unsaid because it is what every account reads: a suffix on every row of every account would
   * carry no information and would make the one account that differs harder to spot.
   */
  const tier =
    tierPreference === "Tier2" ? "" : ` · ${tierLabel(tierPreference)}`;

  return [
    {
      benefit: "FLAT_COPAY",
      label: BENEFIT_LABELS.FLAT_COPAY,
      reads: unset ? "Not set" : `${line}${tier}`,
      onError: unset
        ? "No answer"
        : "Use an earlier check for this member, else blank",
      explainFor: "FLAT_COPAY",
    },
    {
      benefit: "COINSURANCE_PERCENT",
      label: BENEFIT_LABELS.COINSURANCE_PERCENT,
      reads: unset ? "Not set" : line,
      onError: unset
        ? "No answer"
        : "May borrow another service line, then an earlier check",
      explainFor: "COINSURANCE_PERCENT",
    },
    {
      benefit: "PROFESSIONAL_COPAY",
      label: BENEFIT_LABELS.PROFESSIONAL_COPAY,
      reads: physicianCopayBilled
        ? "Surgical: the surgeon's own copay line"
        : "Not billed here",
      onError: physicianCopayBilled ? "Blank" : "—",
      explainFor: "PROFESSIONAL_COPAY",
    },
    {
      benefit: "REMAINING_DEDUCTIBLE",
      label: BENEFIT_LABELS.REMAINING_DEDUCTIBLE,
      reads: `Plan level${tier}`,
      onError: "Blank",
      explainFor: "REMAINING_DEDUCTIBLE",
    },
    {
      benefit: "REMAINING_OOP",
      label: BENEFIT_LABELS.REMAINING_OOP,
      reads: `Plan level${tier}`,
      onError: "Blank",
      explainFor: "REMAINING_OOP",
    },
    {
      benefit: "MRI_CT",
      label: "Estimates run with an MRI/CT service type",
      reads: "MRI/CAT Scan service line, every field",
      onError: "Blank",
      explainFor: null,
    },
    // CLE-3013 — the Diagnostic Medical-scoped rows, every account. The physician copay keeps the
    // account's own row (a Diagnostic Medical response names no physician line).
    {
      benefit: "DIAGNOSTIC_MEDICAL",
      label: "Estimates run with the Diagnostic Medical service type",
      reads: "Diagnostic Medical service line, copay and co-insurance",
      onError: "Blank",
      explainFor: null,
    },
  ];
};

interface IWhatThisAccountReadsProps {
  serviceLine: BenefitServiceLine | null;
  physicianCopayBilled: boolean;
  tierPreference: BenefitTierPreference;
  onExplain: (benefit: string | null) => void;
}

export function WhatThisAccountReads({
  serviceLine,
  physicianCopayBilled,
  tierPreference,
  onExplain,
}: IWhatThisAccountReadsProps) {
  const rows = buildReadsRows(
    serviceLine,
    physicianCopayBilled,
    tierPreference
  );

  const labelCellBody = (row: IReadsRow) => (
    <span className="font-medium text-sm">{row.label}</span>
  );

  const readsCellBody = (row: IReadsRow) => (
    <span data-testid={`benefitSelection.reads.${row.benefit}`}>
      {row.reads}
    </span>
  );

  const errorCellBody = (row: IReadsRow) => <span>{row.onError}</span>;

  const explainCellBody = (row: IReadsRow) => (
    <Button
      type="button"
      label="How it is selected"
      className="p-button-link p-button-sm p-0"
      data-testid={`benefitSelection.reads.${row.benefit}.explain`}
      onClick={() => onExplain(row.explainFor)}
    />
  );

  return (
    <div className="flex flex-column gap-2">
      <span className="font-medium text-sm">
        <span className="text-color-secondary font-normal">
          Read only — the result of the choices above.
        </span>
      </span>
      <StyledDataTable
        value={rows}
        dataKey="benefit"
        data-testid="benefitSelection.readsTable"
        className="w-full"
      >
        <Column header="Benefit" body={labelCellBody} />
        <Column header="Reads" body={readsCellBody} />
        <Column header="If the payer reported an error" body={errorCellBody} />
        <Column header="" style={{ width: "12rem" }} body={explainCellBody} />
      </StyledDataTable>
    </div>
  );
}
